import React from 'react';
import { Award, Shield, Cloud, Brain, Lock } from 'lucide-react';

const Certification = () => {
  const courses = [
    {
      icon: Brain,
      title: 'Artificial Intelligence',
      description: 'Comprehensive AI/ML programs covering Python, TensorFlow, and enterprise AI solutions',
      color: 'from-[#a78bfa] to-[#7c3aed]'
    },
    {
      icon: Cloud,
      title: 'Azure Data Engineer',
      description: 'Microsoft Azure DP-203 certification training with hands-on labs and real-world projects',
      color: 'from-[#0078D4] to-[#00BCF2]'
    },
    {
      icon: Lock,
      title: 'Cyber Security',
      description: 'Industry-recognized security certifications including ethical hacking and SOC analyst training',
      color: 'from-[#f472b6] to-[#ec4899]'
    }
  ];

  return (
    <section id="certification" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A] relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00E5FF] rounded-full blur-[200px] opacity-10"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#1DE9B6] rounded-full blur-[200px] opacity-10"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 backdrop-blur-sm mb-6">
            <Award className="w-4 h-4 text-[#00E5FF]" />
            <span className="text-sm font-medium text-[#00E5FF]">ISO Certified Organization</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 font-['Space_Grotesk']">
            ISO 9001:2015 <span className="text-[#00E5FF]">Certified</span>
          </h2>
          <p className="text-lg text-[#9CA3AF] max-w-3xl mx-auto">
            CloudXAI Technologies is an ISO 9001:2015 certified organization, ensuring quality management in all our training and IT services
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Certificate Details */}
          <div className="space-y-6">
            {/* Certificate Info */}
            <div className="bg-[#111827] border border-[#00E5FF]/20 rounded-2xl p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                  <Shield className="w-7 h-7 text-[#0B0F1A]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white font-['Space_Grotesk']">Quality Management Systems</h3>
                  <p className="text-[#9CA3AF] text-sm">Certificate No: E20260142419</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-[#0B0F1A] rounded-lg p-3">
                  <p className="text-[#9CA3AF]">Valid Until</p>
                  <p className="text-white font-semibold">28/01/2029</p>
                </div>
                <div className="bg-[#0B0F1A] rounded-lg p-3">
                  <p className="text-[#9CA3AF]">Accredited By</p>
                  <p className="text-white font-semibold">IAF & EGAC</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#00E5FF]/10">
                <p className="text-[#9CA3AF] text-sm">
                  <strong className="text-[#00E5FF]">Certified Activities:</strong> Training, Placement Assistance, IT Consulting, Cloud Services, Data Engineering, Cyber Security, AI, DevOps & Software Solutions
                </p>
              </div>
            </div>
          </div>

          {/* Featured Courses */}
          <div>
            <h3 className="text-xl font-bold text-white mb-4 font-['Space_Grotesk']">Featured Certification Courses</h3>
            <div className="space-y-3">
              {courses.map((course, index) => {
                const Icon = course.icon;
                return (
                  <div 
                    key={index}
                    className="bg-[#111827] border border-[#00E5FF]/20 rounded-xl p-4 hover:border-[#00E5FF]/40 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${course.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold group-hover:text-[#00E5FF] transition-colors">{course.title}</h4>
                        <p className="text-[#9CA3AF] text-sm">{course.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'ISO Certified', value: '9001:2015' },
            { label: 'Training Hours', value: '50,000+' },
            { label: 'Placement Rate', value: '95%' },
            { label: 'Corporate Clients', value: '100+' }
          ].map((stat, index) => (
            <div key={index} className="bg-[#111827] border border-[#00E5FF]/20 rounded-xl p-4 text-center hover:border-[#00E5FF]/40 transition-all">
              <p className="text-2xl font-bold text-[#00E5FF] font-['Space_Grotesk']">{stat.value}</p>
              <p className="text-[#9CA3AF] text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certification;
